import { useState, useRef, useEffect, useCallback } from 'react'
import { Send, Bot, User, Sparkles, Loader2 } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { useAgent } from 'agents/react'
import { AGENT_WORKER_HOST, isDev } from '../lib/config'

interface UIMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp?: Date
  parts?: Array<{ type: string; text?: string; [key: string]: unknown }>
}

interface ChatState {
  messages: UIMessage[]
}

const agentHost = isDev ? undefined : AGENT_WORKER_HOST

export default function AIChat() {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [input, setInput] = useState('')
  const [localMessages, setLocalMessages] = useState<UIMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const {
    send,
  } = useAgent<ChatState>({
    agent: 'chat-agent',
    name: 'default',
    host: agentHost,
    onStateUpdate: (newState) => {
      if (newState?.messages) {
        setLocalMessages(newState.messages)
      }
    },
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [localMessages])

  const handleSend = useCallback(() => {
    if (!input.trim() || isLoading) return

    const userMessage: UIMessage = {
      id: uuidv4(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    }

    setLocalMessages(prev => [...prev, userMessage])
    setInput('')
    setIsLoading(true)
    setError(null)

    // Send chat request via WebSocket using the chat protocol
    try {
      send(JSON.stringify({
        type: 'cf_agent_use_chat_request',
        id: uuidv4(),
        init: {
          method: 'POST',
          body: JSON.stringify({
            messages: [...localMessages, userMessage].map(m => ({
              role: m.role,
              content: m.content,
            })),
          }),
        },
      }))
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to send message'))
      setIsLoading(false)
    }
  }, [input, isLoading, localMessages, send])

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <div className="flex items-center gap-3 mb-4">
        <Sparkles className="w-6 h-6 text-orange-500" />
        <h2 className="text-2xl font-bold">AI 对话</h2>
        <span className="text-xs text-gray-400 ml-2">
          {isLoading ? '输入中...' : '就绪'}
        </span>
      </div>

      {error && (
        <div className="mb-2 p-2 bg-red-900/50 text-red-200 rounded text-sm">
          连接错误: {error.message}
        </div>
      )}

      <div className="flex-1 overflow-y-auto bg-gray-800 rounded-lg p-4 mb-4 space-y-4">
        {localMessages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'assistant' && (
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5" />
              </div>
            )}
            <div
              className={`max-w-[70%] rounded-lg p-3 ${
                msg.role === 'user'
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-700 text-gray-100'
              }`}
            >
              <p className="whitespace-pre-wrap">{msg.content}</p>
              <p className="text-xs opacity-60 mt-1">
                {msg.timestamp ? msg.timestamp.toLocaleTimeString() : new Date().toLocaleTimeString()}
              </p>
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5" />
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="flex gap-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex gap-2">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="输入消息..."
          className="flex-1 bg-gray-800 border border-gray-700 rounded-lg p-3 resize-none focus:outline-none focus:border-orange-500"
          rows={2}
          disabled={isLoading}
        />
        <button
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
          className="px-6 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 rounded-lg transition-colors"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
